'use client';

import { CellContext, HeaderContext } from '@tanstack/react-table';
import ToggleHeaderCell from 'src/components/Tables/cells/ToggleHeaderCell';
import { DataTableColumnDef } from 'src/components/primitives/Table/types';
import TriggerConditionTooltip from 'src/components/Tables/cells/TriggerConditionTooltip';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import { AccountOrder } from './types';
import {
	TimeCell,
	MarketCell,
	ActionCell,
	TypeCell,
	SizeCell,
	FilledCell,
	PriceCell,
	ReduceOnlyCell,
	PostOnlyCell,
	TriggerConditionCell,
	EditCell,
} from './cells';

export interface OpenOrdersTableMeta {
	displayDatesAsTimestamp: boolean;
	showAccountValues: boolean;
	onCancelAll: () => void;
}

interface ColumnOptions {
	onToggleDateDisplay: () => void;
}

function getMeta(
	context:
		| CellContext<AccountOrder, unknown>
		| HeaderContext<AccountOrder, unknown>
): OpenOrdersTableMeta {
	return context.table.options.meta as OpenOrdersTableMeta;
}

export function getOpenOrdersColumns(
	options: ColumnOptions
): DataTableColumnDef<AccountOrder>[] {
	const { onToggleDateDisplay } = options;

	return [
		{
			id: 'time',
			accessorKey: 'slot',
			header: () => (
				<ToggleHeaderCell label="Time" onClick={onToggleDateDisplay} />
			),
			meta: {
				width: { ratio: 10, minPx: 100 },
				bodyCellClassName: 'first:xl:pl-md',
				headerCellClassName: 'first:xl:pl-md',
			},
			cell: (context) => {
				const { displayDatesAsTimestamp } = getMeta(context);
				return (
					<TimeCell
						order={context.row.original}
						displayAsTimestamp={displayDatesAsTimestamp}
					/>
				);
			},
		},
		{
			id: 'market',
			accessorKey: 'marketIndex',
			header: 'Market',
			meta: { width: { ratio: 10, minPx: 80 }, bodyCellTestId: 'order-market' },
			cell: ({ row }) => <MarketCell order={row.original} />,
		},
		{
			id: 'action',
			accessorKey: 'direction',
			header: 'Action',
			meta: { width: { ratio: 10, minPx: 80 }, bodyCellTestId: 'order-action' },
			cell: ({ row }) => <ActionCell order={row.original} />,
		},
		{
			id: 'type',
			accessorKey: 'orderType',
			header: 'Type',
			meta: { width: { ratio: 9, minPx: 87 }, bodyCellTestId: 'order-type' },
			cell: ({ row }) => <TypeCell order={row.original} />,
		},
		{
			id: 'size',
			accessorKey: 'baseAssetAmount',
			header: 'Size',
			meta: { width: { ratio: 10, minPx: 92 }, bodyCellTestId: 'order-size' },
			cell: ({ row }) => <SizeCell order={row.original} />,
		},
		{
			id: 'filled',
			accessorKey: 'baseAssetAmountFilled',
			header: 'Filled',
			meta: { width: { ratio: 10, minPx: 80 }, bodyCellTestId: 'order-filled' },
			cell: (context) => {
				const { showAccountValues } = getMeta(context);
				return (
					<FilledCell
						order={context.row.original}
						showAccountValues={showAccountValues}
					/>
				);
			},
		},
		{
			id: 'price',
			header: 'Price',
			meta: { width: { ratio: 10, minPx: 70 }, bodyCellTestId: 'order-price' },
			cell: ({ row }) => <PriceCell order={row.original} />,
		},
		{
			id: 'reduceOnly',
			accessorKey: 'reduceOnly',
			header: 'Reduce Only',
			meta: {
				width: { ratio: 9, minPx: 60 },
				bodyCellTestId: 'order-reduce-only',
			},
			cell: ({ row }) => <ReduceOnlyCell order={row.original} />,
		},
		{
			id: 'postOnly',
			accessorKey: 'postOnly',
			header: 'Post Only',
			meta: {
				width: { ratio: 9, minPx: 60 },
				bodyCellTestId: 'order-post-only',
			},
			cell: ({ row }) => <PostOnlyCell order={row.original} />,
		},
		{
			id: 'triggerCondition',
			accessorKey: 'triggerPrice',
			header: () => (
				<TriggerConditionTooltip>Trigger Condition</TriggerConditionTooltip>
			),
			meta: { width: { ratio: 13, minPx: 100 } },
			cell: ({ row }) => <TriggerConditionCell order={row.original} />,
		},
		{
			id: 'edit',
			meta: {
				width: { fixedPx: 123 },
				bodyCellClassName: 'pl-sm xl:pr-md',
				headerCellClassName: 'pl-sm xl:pr-md',
			},
			header: (context) => {
				const { onCancelAll } = getMeta(context);
				return (
					<div className="flex w-full items-center justify-center">
						<TextButton
							variant="tertiary"
							size="sm"
							onClick={onCancelAll}
							dataTestId="orders-cancel-all"
						>
							<span className="xl:hidden">Cancel All</span>
							<span className="hidden xl:inline">Cancel All Orders</span>
						</TextButton>
					</div>
				);
			},
			cell: ({ row }) => <EditCell order={row.original} />,
		},
	];
}
